import { ISharedPassword, ISharePasswordRequest } from '../types/share.types';
import { config } from '../config';
import { secureStore } from '../utils/secureStore';
import { encryptKeyData } from '../utils/encryption';
import { ShareItemRequest, ShareCategoryRequest, GroupedItems } from '../types/share.types';
import { ISharedItem } from '../types/share.types';
import { handleSessionExpired } from './sessionHandler';

const API_URL = config.API_URL;

export class ShareService {
    // Removed getToken and token logic

    static async sharePassword(request: ISharePasswordRequest): Promise<void> {

        const vaultKey = secureStore.getVaultKey();
        const sharedKey = encryptKeyData(vaultKey);

        const response = await fetch(`${API_URL}/vault/share`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...request,
                sharedKey
            }),
            credentials: 'include', // Send cookies for session auth
        });

        if (!response.ok) {
            if (response.status === 401) {
                handleSessionExpired();
            }
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to share password');
        }
    }

    static async getSharedPasswords(): Promise<ISharedPassword[]> {
        const response = await fetch(`${API_URL}/vault/shared-passwords`, {
            credentials: 'include', // Send cookies for session auth
        });

        if (!response.ok) {
            if (response.status === 401) {
                handleSessionExpired();
            }
            throw new Error('Failed to fetch shared passwords');
        }

        return response.json();
    }

    static async getSharedByMePasswords(): Promise<ISharedPassword[]> {
        const response = await fetch(`${API_URL}/vault/shared-by-me`, {
            credentials: 'include', // Send cookies for session auth
        });

        if (!response.ok) {
            if (response.status === 401) {
                handleSessionExpired();
            }
            throw new Error('Failed to fetch shared by me passwords');
        }

        return response.json();
    }

    static async revokeAccess(shareId: string): Promise<void> {
        const response = await fetch(`${API_URL}/vault/share/${shareId}`, {
            method: 'DELETE',
            credentials: 'include', // Send cookies for session auth
        });

        if (!response.ok) {
            if (response.status === 401) {
                handleSessionExpired();
            }
            throw new Error('Failed to revoke access');
        }
    }

    // static async updatePermissionLevel(shareId: string, permissionLevel: string): Promise<void> {
    //     console.log('shareId', shareId);
    //     console.log('permissionLevel', permissionLevel);
    //     const response = await fetch(`${API_URL}/vault/share/${shareId}/permission`, {
    //         method: 'PUT',
    //         headers: {
    //             'Content-Type': 'application/json',
    //             'Authorization': `Bearer ${this.getToken()}`
    //         },
    //         body: JSON.stringify({ permission_level: permissionLevel })
    //     });

    //     if (!response.ok) {
    //         throw new Error('Failed to update permission level');
    //     }
    // }

    static async updateExpiry(shareId: string, expiresAt: Date | null): Promise<void> {
        const response = await fetch(`${API_URL}/vault/share/${shareId}/expiry`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ expires_at: expiresAt }),
            credentials: 'include', // Send cookies for session auth
        });

        if (!response.ok) {
            throw new Error('Failed to update expiry');
        }
    }

    static async getAllItemTitles(workspaceId?: string): Promise<GroupedItems> {
        try {
            const response = await fetch(`${API_URL}/share/items/all-titles?workspace_id=${workspaceId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Send cookies for session auth
            });

            if (!response.ok) {
                throw new Error('Failed to fetch item titles');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching item titles:', error);
            throw error;
        }
    }

    static async shareItems(request: ShareItemRequest): Promise<any> {
        try {
            const vaultKey = secureStore.getVaultKey();
            const sharedKey = encryptKeyData(vaultKey);
            // Process each item
            const promises = request.itemIds.map(async (item) => {
                const response = await fetch(`${API_URL}/share/item`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        itemId: item.id,
                        itemType: item.type,
                        shareType: request.shareType,
                        sharedWithEmail: request.sharedWithEmail,
                        permissionLevel: request.permissionLevel,
                        expiresAt: request.expiresAt,
                        sharedKey,
                        teamId: request.teamId,
                        workspaceId: request.workspaceId
                    }),
                    credentials: 'include', // Send cookies for session auth
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Failed to share item');
                }

                return await response.json();
            });

            return await Promise.all(promises);
        } catch (error) {
            console.error('Error sharing items:', error);
            throw error;
        }
    }

    static async shareCategory(request: ShareCategoryRequest): Promise<any> {
        try {
            // Generate a shared key for encryption
            const sharedKey = secureStore.getVaultKey();
            const response = await fetch(`${API_URL}/share/category`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    categoryId: request.categoryId,
                    sharedWithEmail: request.sharedWithEmail,
                    permissionLevel: request.permissionLevel,
                    expiresAt: request.expiresAt,
                    sharedKey
                }),
                credentials: 'include', // Send cookies for session auth
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to share category');
            }

            return await response.json();
        } catch (error) {
            console.error('Error sharing category:', error);
            throw error;
        }
    }

    static async getSharedItems(workspaceId: string): Promise<ISharedItem[]> {
        try {
            const response = await fetch(`${API_URL}/share/items/shared-with-me?workspace_id=${workspaceId}`, {
                credentials: 'include', // Send cookies for session auth
            });

            if (!response.ok) {
                if (response.status === 401) {
                    handleSessionExpired();
                    return [];
                }
                throw new Error('Failed to fetch shared items');
            }

            return response.json();
        } catch (error) {
            console.error('Error fetching shared items:', error);
            throw error;
        }
    }

    static async getItemsSharedByMe(workspaceId: string): Promise<ISharedItem[]> {
        try {
            const response = await fetch(`${API_URL}/share/items/shared-by-me?workspace_id=${workspaceId}`, {
                credentials: 'include', // Send cookies for session auth
            });

            if (!response.ok) {
                if (response.status === 401) {
                    handleSessionExpired();
                    return [];
                }
                throw new Error('Failed to fetch shared items');
            }

            return response.json();
        } catch (error) {
            console.error('Error fetching shared items:', error);
            throw error;
        }
    }

    static async revokeItemAccess(id?: string, sharedItemId?: string, type?: string): Promise<void> {
        try {

            if (sharedItemId) {
                const response = await fetch(`${API_URL}/share/item/${id}`, {
                    method: 'DELETE',
                    body: JSON.stringify({ sharedItemId, type }),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include', // Send cookies for session auth
                });

                if (!response.ok) {
                    throw new Error('Failed to revoke access');
                }
            }
            else {
                const response = await fetch(`${API_URL}/share/item/${id}`, {
                    method: 'DELETE',
                    credentials: 'include', // Send cookies for session auth
                });

                if (!response.ok) {
                    throw new Error('Failed to revoke access');
                }
            }
        } catch (error) {
            console.error('Error revoking access:', error);
            throw error;
        }
    }

    static async updateItemExpiry(id: string, expiresAt: Date | null): Promise<void> {
        try {
            const response = await fetch(`${API_URL}/share/item/${id}/expiry`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ expires_at: expiresAt }),
                credentials: 'include', // Send cookies for session auth
            });

            if (!response.ok) {
                throw new Error('Failed to update expiry');
            }
        } catch (error) {
            console.error('Error updating expiry:', error);
            throw error;
        }
    }

    static async updatePermissionLevel(id: string, permissionLevel: string, sharedItemId?: string, type?: string): Promise<void> {
        try {
            console.log('permissionLevel', permissionLevel);
            if (sharedItemId) {
                const response = await fetch(`${API_URL}/share/item/${id}/permission`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ permission_level: permissionLevel, sharedItemId: sharedItemId, type: type }),
                    credentials: 'include', // Send cookies for session auth
                });
                if (!response.ok) {
                    throw new Error('Failed to update permission level');
                }
            } else {
                const response = await fetch(`${API_URL}/share/item/${id}/permission`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ permission_level: permissionLevel }),
                    credentials: 'include', // Send cookies for session auth
                });
                if (!response.ok) {
                    throw new Error('Failed to update permission level');
                }
            }
        } catch (error) {
            console.error('Error updating permission level:', error);
            throw error;
        }
    }
}